/**
 * The permission vocabulary — PART 16.2.2, the single source of truth.
 *
 * Twelve areas x five actions = 60 permissions, named `<area>.<action>`
 * (BR-PERM-37). The guard, the seeds, the role-management API and the future
 * Roles screen ALL import from here; nothing else may define a permission
 * string. The same closed set is CHECK-constrained on role_permissions in
 * 20260806400000_permissions.sql, so the two cannot drift without a failing
 * insert.
 *
 * Two accounts never reach this vocabulary, at opposite ends (16.2.2):
 * the OWNER (a flag, checked first, short-circuits to yes — BR-PERM-66) and a
 * ROLE-LESS user (no role to read; holds none of the 60 and gets only the
 * rung-4 own-record floor — BR-PERM-73/74).
 */
/** The twelve areas of 16.2.2 — one per module, payroll separate from hr on purpose (16.2.3). */
export const PERMISSION_AREAS = [
    'sales',
    'purchasing',
    'inventory',
    'manufacturing',
    'crm',
    'accounting',
    'banking',
    'hr',
    'payroll',
    'projects',
    'reports',
    'admin',
];
/** The five actions of 16.3. */
export const PERMISSION_ACTIONS = ['view', 'create', 'edit', 'delete', 'approve'];
/** All 60, in area-major order — what the Roles screen will draw (BR-PERM-38). */
export const ALL_PERMISSIONS = PERMISSION_AREAS.flatMap((area) => PERMISSION_ACTIONS.map((action) => `${area}.${action}`));
/**
 * BR-PERM-01's four dependent actions. The rule: "Create, Edit, Delete, and
 * Approve must be useless without View. When View is off, the other four must
 * be switched off and greyed out."
 *
 * Read carefully, the rule runs ONE WAY. It says the four are switched OFF
 * when View is off — it does NOT say View switches on when one of them does.
 * So the enforcement is a REFUSAL of the grid, never a silent implication:
 *
 *  - implying View would grant a read nobody ticked, which BR-PERM-63 then
 *    records as the administrator's act when it was the system's, and which
 *    BR-PERM-64 could not bound (an admin lacking `payroll.view` would be the
 *    author of a grant they do not hold);
 *  - refusing keeps the grid the administrator sees and the grid the server
 *    stores the same object, which is the whole point of an access review.
 *
 * The named extensions are deliberately OUT of scope: the rule enumerates the
 * four ACTIONS, and an extension is not one of them (`admin.read_audit_log` is
 * held by the Administrator alongside `admin.view`, but `inventory.see_item_cost`
 * is a different question the spec has not answered — see the block comments
 * on each extension).
 */
const VIEW_DEPENDENT_ACTIONS = ['create', 'edit', 'delete', 'approve'];
/**
 * The areas in which a grid breaks BR-PERM-01 — one of the four dependent
 * actions held while `<area>.view` is not. Empty means the grid is legal.
 *
 * Pure, exported, and the ONE definition: the Roles screen greys switches with
 * it, RolesService refuses saves with it, and the shipped grids of 16.4.2 are
 * pinned against it by test. A locked role that failed it would mean the
 * reading was wrong, not that the role was.
 */
export function viewlessAreas(permissions) {
    const held = new Set(permissions);
    return PERMISSION_AREAS.filter((area) => !held.has(`${area}.view`) &&
        VIEW_DEPENDENT_ACTIONS.some((action) => held.has(`${area}.${action}`)));
}
/** True when the grid satisfies BR-PERM-01 in every one of the twelve areas. */
export function satisfiesViewImplied(permissions) {
    return viewlessAreas(permissions).length === 0;
}
/**
 * ERR-ADM-06-03 — "This role would not be able to open any screen."
 *
 * What OPENS a screen is the twelve `<area>.view` switches, plus the ONE
 * named extension that gates a screen of its own: DSH-09's audit read
 * (BR-DSH-09-13/BR-PERM-52 — `reports.view` does not open the Audit Log,
 * `admin.read_audit_log` does, and the web route table says so). The other
 * four extensions gate actions or columns INSIDE screens some View opens, so
 * a grid carrying only those meets the rule's sentence literally: the role
 * could not open any screen.
 *
 * Pure, exported, one definition: the Roles screen greys Save with it and
 * `RolesService` refuses saves with it. The three locked grids of 16.4.2 all
 * pass (each holds several Views) — pinned by test, because a locked role
 * that failed it would mean this reading was wrong, not the role.
 *
 * The EMPTY grid is deliberately not this rule's business: 16.4.4 creates a
 * new custom role from "an empty grid, or a copy", and on confirming "the
 * new role appears in the rail, selected, with its editor open" — so the
 * empty scaffold must be creatable. ERR-ADM-06-03's own trigger is "Switched
 * off every View permission a role HAS", which is a save against an editor,
 * and that save is the one refused.
 */
export function opensAnyScreen(permissions) {
    return permissions.some((permission) => permission.endsWith('.view') || permission === READ_AUDIT_LOG);
}
/**
 * BR-PERM-01's other half, on the way IN: switching View off must switch the
 * four off with it. Returns the grid with the area's dependent actions
 * removed — the Roles screen calls it so the illegal state is never reachable
 * by hand, and the server's refusal stays the backstop it should be.
 */
export function clearDependentActions(permissions, area) {
    const dependents = new Set(VIEW_DEPENDENT_ACTIONS.map((action) => `${area}.${action}`));
    return permissions.filter((permission) => !dependents.has(permission));
}
const PERMISSION_SET = new Set(ALL_PERMISSIONS);
export function isPermission(value) {
    return PERMISSION_SET.has(value);
}
/**
 * The ONE named extension to the 12×5 grid: owner ruling R10 / BR-FIN-10-16
 * — editing a customer's bounced-cheque flag. The partners migration flagged
 * that this grant "sits outside the 12×5 grid and arrives with the cheque
 * screens"; it arrives HERE so it stays a role_permissions row, visible on
 * the Roles screen (BR-PERM-37) and reviewable like everything else. Like
 * payroll.*, NO shipped role holds it — the Owner grants it deliberately
 * (BR-PERM-64: nobody grants what they do not hold).
 */
export const OVERRIDE_BOUNCED_FLAG = 'banking.override_bounced_flag';
/**
 * The SECOND named extension: reopening a closed accounting period.
 *
 * Two spec statements have to hold at once and neither can be dropped.
 * GEN-94: "Reopening a closed period must be allowed only to a user whose
 * ROLE GRANTS IT, and must write one Audit Log row." EC-19-21: "Reopening
 * must be restricted to the account carrying the OWNER FLAG and must be
 * written to the Audit Log." A plain `accounting.*` action satisfies the
 * first and breaks the second (the Accountant holds `accounting.approve` by
 * default, so a default role would carry it). An owner-only hard-code
 * satisfies the second and breaks the first.
 *
 * The extension pattern satisfies both, exactly as it does for R10's bounced
 * flag: a real role_permissions row, visible on the Roles screen and in an
 * access review (BR-PERM-37), held by NO shipped role — so out of the box the
 * Owner is the only account that can reopen anything (EC-19-21), and an Owner
 * who wants their accountant to hold it grants it deliberately (GEN-94). Like
 * `payroll.*`, nobody can grant what they do not hold (BR-PERM-64), so the
 * grant can only ever originate with the Owner.
 */
export const REOPEN_PERIOD = 'accounting.reopen_period';
/**
 * The THIRD named extension: seeing what an item COST us (MD-03 §9,
 * OQ-05-14 answered "Yes").
 *
 * MD-03 field 13 ships a Hide cost switch, and the spec says plainly what it
 * is worth: "Hide cost is not a permission. Any user can switch it off and
 * read the cost. It hides a number from a shoulder-surfer, not from a
 * person." The label on that field says "Internal", which is a promise the
 * switch does not keep — so the cost is gated the way payroll's figures are:
 * the read that is not entitled to it NEVER SELECTS the column, and the key
 * is absent from the response rather than masked in it. A number that
 * reaches the browser is a number the browser can be made to show.
 *
 * It is an extension rather than an action because `inventory.view` is held
 * by every role that touches a warehouse, and margin is not warehouse
 * business. Held by NO shipped role: the Owner grants it deliberately, and
 * BR-PERM-64 means the grant can only ever originate with them.
 *
 * BR-DSH-07-14 rides the same grant: without it the Inventory report is
 * shown with its cost, value, turnover and ABC content REMOVED, never
 * refused outright.
 */
export const SEE_ITEM_COST = 'inventory.see_item_cost';
/**
 * The FOURTH named extension: reading the Audit Log (DSH-09).
 *
 * BR-DSH-09-13 is unusually specific about the shape, not just the holder:
 * "Who may read the audit log must be a role permission OF ITS OWN, separate
 * from being an Administrator or Owner." A plain `admin.view` would fail that
 * on both counts — it is not its own switch, and every Accountant holds it by
 * default (16.4.2 row 12 gives them `R` on admin), so the company's books
 * would come with a reader of everybody's actions attached.
 *
 * So it joins the closed vocabulary the way the other three did: a real
 * `role_permissions` row, drawn on the Roles screen, visible in an access
 * review (BR-PERM-37), and revocable on its own. It sits in the `admin` AREA
 * because BR-PERM-52 puts the log's views, export and retention there — which
 * is the same statement one level up, and is why the Reports group's Audit Log
 * entry does not follow `reports.view`.
 *
 * UNLIKE the other three, one shipped role holds it: DSH-09 §9 says "No
 * shipped role but Admin carries it by default; the Owner always holds it
 * (BR-PERM-04)." That is not a contradiction of BR-DSH-09-13 — the rule asks
 * for a separate switch, and this is one; an Owner who does not want their
 * administrator reading the log duplicates the role and clears it. The two
 * shipped roles that hold `admin.view` without holding this (Accountant reads
 * settings; AP Clerk holds no admin at all) are the proof the switch is real.
 */
export const READ_AUDIT_LOG = 'admin.read_audit_log';
/**
 * The FIFTH named extension: changing a number sequence (MD-13 §9).
 *
 * "One screen, and the narrowest permission in the product: Number sequences,
 * which only an Administrator holds by default."
 *
 * It cannot be an action on the twelve-by-five grid, because MD-13 wants two
 * different answers out of one area and the grid can only give one:
 *
 *   §2 / §6  the ACCOUNTANT reads the next number and runs Check for gaps —
 *            which they can, because 16.4.2 row 12 gives them `admin.view`;
 *   §6       Edit and Save are the Administrator's alone, and `admin.edit` is
 *            the same switch that renames a warehouse, so it cannot be the
 *            gate for the one screen whose settings decide what every document
 *            in the company is called.
 *
 * So reading is `admin.view` and writing is this. Seeded to Administrator like
 * DSH-09's audit read, and to no other shipped role: the Accountant holds
 * `admin.view` WITHOUT holding this, which is what makes the separate switch a
 * real one rather than a synonym.
 */
export const CHANGE_NUMBER_SEQUENCES = 'admin.number_sequences';
/**
 * The SIXTH named extension: posting a sale that drives stock BELOW ZERO.
 *
 * BR-INV-04-08 / BR-INV-06-13 let on-hand go negative only in a warehouse
 * that allows it (MD-08 field 16). Until this grant existed the warehouse
 * flag was the WHOLE gate: any holder of `sales.approve` could confirm a
 * delivery — or post a direct item invoice — against goods the shelf did not
 * hold, and the ledger booked cost of sales for stock nobody had counted in.
 * A warehouse flag says the LOCATION tolerates it; it says nothing about
 * who may decide to. This is the "who": the warehouse must allow it AND the
 * confirming person must hold this, else the posting is refused by name
 * (`STOCK_REFUSALS.negativeStockNeedsPermission`), and the confirming act
 * may carry a typed reason that rides the audit row.
 *
 * An extension rather than an action because `sales.approve` is held by
 * every role that confirms anything in sales, and overriding the shelf is a
 * narrower trust than confirming a sale. Seeded to the Administrator the way
 * MD-13's number-sequence grant is (the Administrator "holds everything
 * except payroll", and an override every confirmer held yesterday must not
 * vanish from the one role meant to hold it); the Accountant holds
 * `sales.approve` WITHOUT this, which is what makes the separate switch a
 * real one. The Owner holds it on the flag (BR-PERM-66).
 */
export const OVERRIDE_NEGATIVE_STOCK = 'inventory.override_negative_stock';
/**
 * The SEVENTH named extension: raising a goods receipt with NO purchase
 * order behind it (wave 5C, ERP gap analysis Oct 2026).
 *
 * PUR-02 raises every receipt against an order (BR-PUR-02-13), and that is
 * the shape in which something upstream vouches for what entered stock and
 * at what price — an approved order, and a three-way match when the bill
 * comes. A direct receipt has neither: the goods, the supplier and the cost
 * are typed at the door. `purchasing.create` is held by every role that
 * types a receipt (the AP Clerk, the Accountant), and receiving goods nobody
 * ordered is a narrower trust than receiving goods somebody approved — so
 * it is its own switch, refused by name (`GRN_MESSAGES.directNeedsPermission`)
 * when the caller lacks it. Seeded to the Administrator the way the
 * negative-stock override is; the Accountant and the AP Clerk hold
 * `purchasing.create` WITHOUT this, which is what makes the separate switch
 * a real one. The Owner holds it on the flag (BR-PERM-66).
 */
export const RECEIVE_WITHOUT_ORDER = 'purchasing.receive_without_order';
export const EXTRA_PERMISSIONS = [
    OVERRIDE_BOUNCED_FLAG,
    REOPEN_PERIOD,
    SEE_ITEM_COST,
    READ_AUDIT_LOG,
    CHANGE_NUMBER_SEQUENCES,
    OVERRIDE_NEGATIVE_STOCK,
    RECEIVE_WITHOUT_ORDER,
];
export const ALL_GRANTABLE_PERMISSIONS = [
    ...ALL_PERMISSIONS,
    ...EXTRA_PERMISSIONS,
];
// ── The three shipped roles and their locked grids (16.4.2) ─────────────────
export const SYSTEM_ROLE_NAMES = ['Administrator', 'Accountant', 'AP Clerk'];
/** 16.4.2's letters: R view · C create · U edit · D delete · A approve. */
const LETTER_TO_ACTION = {
    R: 'view',
    C: 'create',
    U: 'edit',
    D: 'delete',
    A: 'approve',
};
function expand(grid) {
    return PERMISSION_AREAS.flatMap((area) => [...grid[area]].map((letter) => {
        const action = LETTER_TO_ACTION[letter];
        if (!action)
            throw new Error(`bad grid letter: ${letter}`);
        return `${area}.${action}`;
    }));
}
/**
 * The default grant matrix, transcribed row by row from PART 16.4.2 (owner
 * ruling R18; defaults proposed under OQ-16-01). These grids are seeded per
 * tenant at signup and then LOCKED (BR-PERM-41/70) — a company customizes by
 * duplicating, never by editing these.
 *
 * **BR-PERM-42 is RETIRED** (owner ruling R18, 2 Aug 2026) and is recorded
 * here rather than built. It made shipped grids editable except for Owner and
 * Administrator — a starting point rather than a fixed truth. R18 replaced it
 * with BR-PERM-70 and BR-PERM-71: all three shipped grids are locked outright
 * and Duplicate is the customization path. Nothing implements BR-PERM-42, and
 * code that made any of these three grids editable would be reintroducing a
 * retired rule, not honouring one.
 *
 * The rows that carry the argument (16.4.2's own reasoning):
 *  - `payroll` is empty across the whole table — rulings R15/R18 restated:
 *    salary access is the Owner flag's alone until the Owner grants it
 *    (BR-PERM-39), and since nobody may grant what they do not hold
 *    (BR-PERM-64), only the Owner can ever create a payroll-reading role.
 *    **BR-PERM-03 is the rule this replaces and it is WITHDRAWN** (16.2, in
 *    terms: "This supersedes BR-PERM-03… BR-PERM-03 is withdrawn"). It blocked
 *    payroll with a hard rule OUTSIDE the grid because the old five-area model
 *    had no room for it; the twelve-area model has a `payroll` area, so the
 *    block is this empty row — visible to an administrator and answerable at
 *    an access review. A build must not reintroduce an out-of-grid payroll
 *    check: nothing to do here beyond keeping the row empty.
 *  - The Administrator holds everything EXCEPT payroll.
 *  - The Accountant approves on sales/accounting/banking, creates-and-edits
 *    on purchasing/crm without approving, reads inventory/manufacturing/
 *    projects for valuation, holds NOTHING on hr or payroll, deletes nothing.
 *  - The AP Clerk cannot see sales at all, cannot open settings (no admin).
 *  - Delete belongs to management: only the Administrator holds it.
 */
export const SYSTEM_ROLE_GRANTS = {
    //                       #   area            Administrator  Accountant  AP Clerk
    Administrator: [...expand({
            sales: 'RCUDA', //     1   sales           RCUDA
            purchasing: 'RCUDA', //2   purchasing      RCUDA
            inventory: 'RCUDA', // 3   inventory       RCUDA
            manufacturing: 'RCUDA', // 4 manufacturing RCUDA
            crm: 'RCUDA', //       5   crm             RCUDA
            accounting: 'RCUDA', //6   accounting      RCUDA
            banking: 'RCUDA', //   7   banking         RCUDA
            hr: 'RCUDA', //        8   hr              RCUDA
            payroll: '', //        9   payroll         —      (BR-PERM-39)
            projects: 'RCUDA', // 10   projects        RCUDA
            reports: 'RCUDA', //  11   reports         RCUDA
            admin: 'RCUDA', //    12   admin           RCUDA
            // DSH-09 §9: "No shipped role but Admin carries it by default." The one
            // named extension any shipped grid holds — see READ_AUDIT_LOG above for
            // why it is an extension rather than an admin action.
            //
            // MD-13 §2's table makes the second: "Administrator — sets the prefix and
            // the starting number when the business starts — Yes". The Accountant
            // holds `admin.view` and NOT this, which is the whole point of it being
            // its own switch (§9: "the narrowest permission in the product").
            //
            // The third: overriding the shelf (OVERRIDE_NEGATIVE_STOCK above). The
            // Accountant confirms deliveries and does NOT hold this.
            //
            // The fourth: a goods receipt with no order behind it
            // (RECEIVE_WITHOUT_ORDER above). The Accountant and the AP Clerk type
            // receipts and do NOT hold this.
        }), READ_AUDIT_LOG, CHANGE_NUMBER_SEQUENCES, OVERRIDE_NEGATIVE_STOCK, RECEIVE_WITHOUT_ORDER],
    Accountant: expand({
        sales: 'RCUA', //      1                                  RCUA
        purchasing: 'RCU', //  2                                  RCU
        inventory: 'R', //     3                                  R
        manufacturing: 'R', // 4                                  R
        crm: 'RCU', //         5                                  RCU
        accounting: 'RCUA', // 6                                  RCUA
        banking: 'RCUA', //    7                                  RCUA
        hr: '', //             8                                  —
        payroll: '', //        9                                  —
        projects: 'R', //     10                                  R
        reports: 'RC', //     11                                  RC
        admin: 'R', //        12                                  R
    }),
    'AP Clerk': expand({
        sales: '', //          1                                             —
        purchasing: 'RCU', //  2                                             RCU
        inventory: 'R', //     3                                             R
        manufacturing: '', //  4                                             —
        crm: 'R', //           5                                             R
        accounting: '', //     6                                             —
        banking: '', //        7                                             —
        hr: '', //             8                                             —
        payroll: '', //        9                                             —
        projects: '', //      10                                             —
        reports: 'R', //      11                                             R
        admin: '', //         12                                             —
    }),
};
/** Shown on the Roles screen; also the seeded roles.description. */
export const SYSTEM_ROLE_DESCRIPTIONS = {
    Administrator: 'Runs the system day to day. Everything except payroll.',
    Accountant: 'Keeps the books. Approves on sales, accounting and banking; deletes nothing.',
    'AP Clerk': 'Enters supplier bills and expenses. No sales, no settings.',
};
// ── The fixed sentences of PART 16 ──────────────────────────────────────────
// Wording is part of the spec; the constants live here so UI and API reuse
// rather than retype (same policy as AUTH_MESSAGES).
export const PERM_MESSAGES = {
    /**
     * 16.7 — the deny. Says a permission is lacking and who to ask; NEVER which
     * permission the caller holds or what the grid contains.
     */
    denied: 'You do not have permission to do this. Ask your administrator.',
    /**
     * 16.7 row 1 — the PAGE, which is a different sentence from the deny above
     * and deliberately so: `denied` answers an attempted action, this answers an
     * attempted door. "If they type the address, show the 'no permission' page:
     * *'You do not have permission to open this page. Ask your administrator.'*"
     *
     * It names no permission, no module and no subscription. §1.5.2 is explicit
     * about the last of those: "A user who may not see Projects is told about
     * the permission, not about the subscription — nobody should learn what
     * their company has and has not bought from an error message meant for
     * someone else."
     */
    deniedPage: 'You do not have permission to open this page. Ask your administrator.',
    /**
     * 16.7 row 4 / BR-PERM-73 — what a person holding NO ROLE meets on the
     * landing page. "Hide every menu group, and say why on the landing page
     * rather than showing an empty shell… An empty product with no explanation
     * reads as a broken product."
     */
    noRoleYet: 'You do not have a role yet, so there is not much here. Ask your administrator or the account owner to give you one.',
    /**
     * BR-PERM-17/-18 — the sentence beside a button a role cannot use. 16.7 row
     * 2: "Show the screen and grey out the button. Resting the pointer on it
     * must say why: *'Your role cannot delete bills.'* The button must never
     * vanish, or the person cannot tell whether the feature exists."
     *
     * The caller supplies the verb phrase ("delete bills") so the SHAPE of the
     * sentence is fixed in one place and cannot drift screen by screen.
     */
    roleCannot: (what) => `Your role cannot ${what}.`,
    /** BR-PERM-70 — an attempt to edit/deactivate/delete a shipped role. */
    lockedRole: (name) => `${name} ships with the product and cannot be changed. Duplicate it to make a version you can edit.`,
    /**
     * BR-PERM-72 — only the Owner grants or revokes Administrator. The spec
     * quotes this sentence twice, once per route it can be reached by:
     * ERR-ADM-05-10 (the Users screen) and ERR-ADM-06-09 (the Roles screen).
     * Both are described as unreachable through the picker, which omits the
     * option — this is the refusal for every OTHER route, which is where a
     * privilege escalation would actually be attempted.
     */
    onlyOwnerAssignsAdmin: 'Only the owner can make somebody an admin.',
    /**
     * BR-ADM-06-08 — "A user must not change their own role, and must not grant
     * themselves anything through a role they create or edit (BR-PERM-64)."
     * The spec fixes this sentence; it is quoted here verbatim.
     *
     * WHY IT IS A SEPARATE REFUSAL FROM `grantNotHeld`. BR-PERM-64 bounds what a
     * grid may CONTAIN when somebody creates or edits a role, and `assertGrantable`
     * enforces it. It says nothing about which role a person is given, so an
     * account holding `admin.edit` through a CUSTOM role — outside BR-PERM-72's
     * Administrator bound entirely — could assign ITSELF any other active role,
     * including one the Owner had built holding `payroll.view`. The holder could
     * not BUILD that role and could simply TAKE it, which leaves BR-PERM-64's
     * stated purpose ("this is what stops an admin writing themselves a route to
     * payroll") unmet by a route that never touches a grid.
     *
     * It is UNCONDITIONAL, and deliberately not "refuse only when the change
     * grants more": a rule written that way is one somebody walks around in two
     * steps, and the spec's clause has no such qualifier.
     *
     * THE OWNER IS NOT EXEMPT, and that is the spec's answer rather than a
     * choice made here. BR-ADM-06-08's second sentence ("Only the Owner, or an
     * admin acting on somebody else…") reads as though they might be;
     * BR-ADM-05-05 settles it on the other screen — the owner's account "must
     * not be suspended, removed, HAVE ITS ROLE CHANGED OR REMOVED, or be reduced
     * in any way, by anybody INCLUDING THEMSELVES (BR-PERM-67)".
     */
    ownRoleChange: 'You cannot change your own permissions.',
    /**
     * BR-PERM-59 / ERR-ADM-06-07 / EC-15-03 — deactivation requires reassigning
     * every holder first, and the refusal NAMES how many. EC-15-03 adds the two
     * things the system must not do instead: silently move them to another role,
     * or leave them with no role at all.
     *
     * ERR-ADM-06-07's wording is the one built ("4 people still hold this role.
     * Move them to another role first."); EC-15-03 quotes a shorter variant of
     * the same sentence. This is also EC-19-08's shape for the whole product:
     * a delete (or deactivation) of a thing others point at is REFUSED naming
     * what is in the way and how many — with PART 17.3.2's RESTRICT foreign
     * keys as the database floor beneath every such message.
     */
    roleInUse: (holders) => `${holders} ${holders === 1 ? 'person still holds' : 'people still hold'} this role. Move them to another role first.`,
    /** BR-PERM-60 — a deactivated role must not be assignable. */
    roleDeactivated: 'This role is deactivated. Reactivate it before assigning it.',
    /** BR-PERM-64 / ERR-ADM-06-08 — nobody grants a permission they do not hold themselves. */
    grantNotHeld: 'You cannot grant a permission you do not hold yourself.',
    /**
     * BR-PERM-01 — a grid holding Create, Edit, Delete or Approve in an area
     * whose View is off. The rule is fixed by the spec; this SENTENCE is not
     * (PART 16.7 gives no row for it), so it is written here once, in the shape
     * of the rest: what is wrong, and the two ways out. Static rather than
     * area-parameterised so it can be translated by exact match like the others
     * — the screen names the area, because the screen can point at the row.
     */
    viewRequired: 'Create, Edit, Delete and Approve do nothing without View. Switch View on for that area, or switch them off.',
    /**
     * ERR-ADM-06-03 — every View switched off. The spec's own sentence, held
     * verbatim (spec-wording.spec.ts pins the literal string): the ADM-06 error
     * table row "Switched off every View permission a role has". See
     * `opensAnyScreen` for what counts as opening a screen and why the empty
     * scaffold of 16.4.4's create flow is outside the rule.
     */
    noViewLeft: 'This role would not be able to open any screen. Leave at least one View permission on.',
    /**
     * BR-PERM-62 / ERR-ADM-06-02 — names unique within the company, shipped
     * names included. The spec's row writes the sentence WITH the colliding
     * name in it — "A role called Accountant already exists. Pick another
     * name." — so this is a function like `lockedRole`, not a constant: the
     * caller supplies the name that collided and the shape cannot drift. Like
     * every parameterised sentence it passes through untranslated
     * (server-messages.ts's documented policy) — the price of naming the thing
     * rather than gesturing at "this name".
     */
    roleNameTaken: (name) => `A role called ${name} already exists. Pick another name.`,
};
/**
 * D46 — BR-PERM's three parameterised role refusals, structured. `lockedRole`
 * and `roleNameTaken` carry a role name; `roleInUse`'s `holders` is a genuine
 * count (raw), its plural forking the whole noun-and-verb ("person still holds"
 * / "people still hold"). Sentence parity pinned by `refusal-census.spec.ts`.
 */
export const PERM_REFUSALS = {
    lockedRole: (name) => ({
        message: PERM_MESSAGES.lockedRole(name),
        key: 'roles.errLockedRole',
        params: { name },
    }),
    roleInUse: (holders) => ({
        message: PERM_MESSAGES.roleInUse(holders),
        key: 'roles.errRoleInUse',
        params: { holders },
    }),
    roleNameTaken: (name) => ({
        message: PERM_MESSAGES.roleNameTaken(name),
        key: 'roles.errRoleNameTaken',
        params: { name },
    }),
};
//# sourceMappingURL=permissions.js.map